import React, { useEffect, useRef, useState } from 'react';
import { Reorder } from 'framer-motion';
import { usePortal } from '../../contexts/PortalContext';
import { createStep, deleteStep, fetchProjectSteps, setStepOrder, setStepStatus, updateStep, type ProjectStep, type StepDraft, type StepStatus } from '../../utils/projectSteps';
import { StepForm } from './StepForm';
import { StepRow } from './StepRow';

/**
 * THE PROJECT TRACKER, from the studio side. Add a step, edit it, date it,
 * say whose it is and who exactly, set its stage and where it stands, move
 * it, remove it.
 *
 * **Four files, for one reason.** `pages/portal/ProjectAdmin.tsx` is past the
 * size this editor can write back in one piece, so the panel lives here, the
 * form in `StepForm.tsx` and the row in `StepRow.tsx`. A file that cannot be
 * safely written is a file that cannot be safely fixed.
 *
 * **Dragged by a handle, not moved by buttons — 14 Oct 2026, Tom's call.**
 * The deliverables list on this same page already drags by a grip, and two
 * lists on one screen that reorder differently is exactly the convolution
 * this OS is meant to avoid.
 *
 * **The order is written once, on drop.** `onReorder` fires continuously
 * while a row is in the air; persisting there would mean one UPDATE per row
 * per frame. The list moves locally during the drag and the database is told
 * when the row lands — and only if the order actually changed.
 *
 * **One list, not three.** The client page groups by stage; this one stays a
 * single draggable list, because order is a property of the whole tracker
 * and three separate lists would mean three separate drags to move a step
 * from pre-production into delivery. Keep the stages contiguous when
 * dragging and the client page follows.
 */

const EMPTY: StepDraft = {
  title: '',
  note: '',
  owner: 'portal',
  ownerName: '',
  phase: 'pre',
  start: '',
  end: ''
};
const sameOrder = (a: ProjectStep[], b: ProjectStep[]) => a.length === b.length && a.every((row, i) => row.id === b[i].id);
export function ProjectSteps() {
  const {
    activeProject,
    notify
  } = usePortal();
  const projectId = activeProject.id;
  const [steps, setSteps] = useState<ProjectStep[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [carryingId, setCarryingId] = useState<string | null>(null);

  /* The drag handlers fire from inside a child and must see the list as it is
     at that moment, not as it was when the handler was created. */
  const liveSteps = useRef<ProjectStep[]>([]);
  const beforeDrag = useRef<ProjectStep[] | null>(null);
  useEffect(() => {
    liveSteps.current = steps;
  }, [steps]);
  useEffect(() => {
    let live = true;
    void fetchProjectSteps(projectId).then(rows => {
      if (live) setSteps(rows);
    });
    return () => {
      live = false;
    };
  }, [projectId]);
  const reload = async () => setSteps(await fetchProjectSteps(projectId));
  async function add(draft: StepDraft) {
    setBusy(true);
    const {
      result
    } = await createStep(projectId, draft, steps.length);
    setBusy(false);
    if (!result.ok) return notify(`Could not add the step — ${result.error}`);
    setAdding(false);
    await reload();
    notify('Step added');
  }
  async function save(id: string, draft: StepDraft) {
    setBusy(true);
    const result = await updateStep(id, draft);
    setBusy(false);
    if (!result.ok) return notify(`Could not save — ${result.error}`);
    setEditingId(null);
    await reload();
    notify('Step saved');
  }
  async function remove(id: string) {
    setBusy(true);
    const result = await deleteStep(id);
    setBusy(false);
    setConfirmId(null);
    if (!result.ok) return notify(`Could not remove — ${result.error}`);
    await reload();
    notify('Step removed');
  }

  /* The status moves at once and goes back if the write is refused. No toast
     on success: working down a list of statuses should not produce a column
     of notifications. */
  async function status(id: string, next: StepStatus) {
    const before = liveSteps.current;
    setSteps(before.map(row => row.id === id ? {
      ...row,
      status: next
    } : row));
    const result = await setStepStatus(id, next);
    if (!result.ok) {
      setSteps(before);
      notify(`Could not update — ${result.error}`);
    }
  }
  function lift(id: string) {
    beforeDrag.current = liveSteps.current;
    setCarryingId(id);
  }

  /* Optimistic: the row is already where it was dropped. If the write is
     refused the list goes back to where it started, because a list that says
     one thing while the database says another is worse than a failed move. */
  async function drop() {
    setCarryingId(null);
    const before = beforeDrag.current;
    beforeDrag.current = null;
    const next = liveSteps.current;
    if (!before || sameOrder(before, next)) return;
    const result = await setStepOrder(next.map(row => row.id));
    if (!result.ok) {
      setSteps(before);
      notify(`Could not reorder — ${result.error}`);
    }
  }

  /* A new step joins the end of the list, so it starts in the stage the end
     of the list is in — adding to a delivery schedule should not default to
     pre-production. */
  const nextDraft: StepDraft = steps.length === 0 ? EMPTY : {
    ...EMPTY,
    phase: steps[steps.length - 1].phase
  };
  return <section aria-labelledby="steps-admin" className="mt-16" data-id="element-1249" data-mp="2bynioadgqtnv">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-1250" data-mp="2702ey0z05ruo">
        <h2 id="steps-admin" className="label m-0 text-paper/40" data-id="element-1251" data-mp="2l04u3ffxe768">
          Project tracker
        </h2>
        <p className="label-sm m-0 text-paper/35" data-id="element-1252" data-mp="2fz7glg5v21b2">
          {steps.length === 0 ? 'Shown on the client page' : `${steps.length} step${steps.length === 1 ? '' : 's'} · shown on the client page`}
        </p>
      </div>

      {steps.length === 0 && !adding && <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-1253" data-mp="2p0ogo9czxmcj">
          Nothing added yet.
        </p>}

      <Reorder.Group as="ul" axis="y" values={steps} onReorder={setSteps} className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-1254" data-mp="2u2xcddh972ic">
        {steps.map(step => <StepRow key={step.id} step={step} clientName={activeProject.client} editing={editingId === step.id} confirming={confirmId === step.id} busy={busy} onLift={() => lift(step.id)} onDrop={drop} onStatus={next => status(step.id, next)} onEdit={() => {
        setAdding(false);
        setConfirmId(null);
        setEditingId(step.id);
      }} onCancelEdit={() => setEditingId(null)} onSave={draft => save(step.id, draft)} onAskRemove={() => setConfirmId(step.id)} onCancelRemove={() => setConfirmId(null)} onRemove={() => remove(step.id)} data-id="element-1255" data-mp="269f5zwrhorjd" />)}
      </Reorder.Group>

      {adding ? <div className="border-b border-ruleDark py-6" data-id="element-1256" data-mp="1631vwaqxb0oc">
          <StepForm initial={nextDraft} busy={busy} submitLabel="Add Step" onCancel={() => setAdding(false)} onSubmit={add} data-id="element-1257" data-mp="162idksxd2a9s" />
        </div> : <button type="button" onClick={() => {
      setEditingId(null);
      setAdding(true);
    }} className="label mt-8 flex items-center gap-3 border border-paper/30 px-6 py-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-1258" data-mp="wb5qsfqf6aam">
          + Add Step
        </button>}
    </section>;
}